import React, { useState, useEffect, Fragment } from "react";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { setDefaultHeader } from "utils/httpClient";
import { useDispatch, useSelector } from "react-redux";
import { jwtTokenGenrate, userLogin } from "reduxStore/action/authActions";
import strings from "translate/language";
import { emailAddressValid, handleDetailResponse, loginMustChangePassword, seo } from "utils/utils";
import { getPermission } from "reduxStore/action/permissionActions";
import { AdminRoleId, MIS_RoleId, WEB_APP_VERSION, getWebEnvironmentBanner } from "utils/constants";
import "./Login.css";

const Signin = () => {
  const navigate = useNavigate();
  const [loginDetail, setLoginDetail] = useState({
    device_id: "abc",
    device_type: "web",
    login_type: 3,
    app_version: WEB_APP_VERSION,
  });
  const [passwordShown, setPasswordShown] = useState(false);
  const [errorMessage, setErrorMessage] = useState({});

  const dispatch = useDispatch();
  const loginSelector = useSelector((state) => state.login);

  useEffect(() => {
    checklogin();
  }, [loginSelector]);

  const handleEnterPressFromFirst = (event) => {
    if (event.key === "Enter") {
      const next = document.getElementById("focus_2");
      next?.focus();
    }
  };

  const handleEnterPressFromSecond = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      loginAuth();
    }
  };

  const checklogin = async () => {
    if (loginSelector.response && loginSelector.authToken) {
      if (loginSelector.response.status === 200) {
        await setDefaultHeader("token", loginSelector.response.token);
        dispatch(
          getPermission({
            isAdmin:
              handleDetailResponse(loginSelector.response.data).role_id ===
                AdminRoleId ||
              handleDetailResponse(loginSelector.response.data).role_id ===
                MIS_RoleId,
            role_id: handleDetailResponse(loginSelector.response.data).role_id,
            isMIS:
              handleDetailResponse(loginSelector.response.data).role_id ===
              MIS_RoleId,
          })
        );
        navigate(
          loginMustChangePassword(loginSelector.response)
            ? "/changepassword"
            : "/dashboard",
        );
      }
    } else {
      if (loginSelector.response == null) {
        dispatch(jwtTokenGenrate());
      } else {
        await setDefaultHeader("token", loginSelector.response.token);
      }
    }
  };

  const togglePassword = () => {
    setPasswordShown(!passwordShown);
  };

  const validation = () => {
    let isError = true;
    let nextErrors = {};
    if (loginDetail.email === undefined || loginDetail.email === "") {
      isError = false;
      nextErrors.email = strings.loginemail;
    } else if (!emailAddressValid(loginDetail.email)) {
      isError = false;
      nextErrors.email = strings.emailValidationErr;
    }
    if (loginDetail.password === undefined || loginDetail.password === "") {
      isError = false;
      nextErrors.password = strings.loginpassword;
    }
    setErrorMessage(nextErrors);
    return isError;
  };

  const loginAuth = async () => {
    if (validation()) {
      dispatch(userLogin(loginDetail));
    }
  };

  useEffect(() => {
    seo({
      title: "Login",
      metaDescription: "Login",
      canonicalLink: window.location.href,
    });
  }, []);

  const canSubmit =
    Boolean(loginDetail.email?.trim()) && Boolean(loginDetail.password);
  const envBanner = getWebEnvironmentBanner();

  return (
    <Fragment>
      <div className="page-wrapper justo-login-page">
        <div className="container-fluid p-0">
          <div className="authentication-main justo-login">
            <div className="justo-login__backdrop" aria-hidden="true">
              <div className="justo-login__backdrop-grid" />
            </div>

            <div className="justo-login__content">
              <div className="justo-login__panel">
                <aside className="justo-login__visual" aria-hidden="false">
                  <div className="justo-login__visual-media">
                    <img
                      src="/assets/images/property.jpg"
                      alt=""
                      className="justo-login__visual-img"
                    />
                    <div className="justo-login__visual-shade" />
                  </div>
                  <div className="justo-login__visual-copy">
                    <p className="justo-login__visual-eyebrow">JUSTO</p>
                    <h2 className="justo-login__visual-title">
                      {strings.loginHeroHeadline}
                    </h2>
                    <p className="justo-login__visual-support">
                      {strings.loginHeroSupport}
                    </p>
                  </div>
                </aside>

                <section className="justo-login__form-pane">
                  <div className="justo-login__form-inner">
                    <div className="justo-login__brand-wrap">
                      <img
                        className="justo-login__brand"
                        src="/assets/images/JUSTOLOGO.jpg"
                        alt="Justo"
                      />
                      <p className="justo-login__tagline">
                        {strings.companyTagline}
                      </p>
                      {envBanner ? (
                        <p className="justo-login__env-banner" role="status">
                          {envBanner}
                        </p>
                      ) : null}
                    </div>

                    <div className="justo-login__intro">
                      <h1 className="justo-login__title">{strings.Login}</h1>
                      <p className="justo-login__subtitle">
                        {strings.enterUsernameAndPass}
                      </p>
                    </div>

                    <form
                      className="justo-login__form"
                      onSubmit={(e) => {
                        e.preventDefault();
                        loginAuth();
                      }}
                    >
                      <div className="justo-login__field">
                        <label className="justo-login__label" htmlFor="focus_1">
                          {strings.username}
                          <span className="justo-login__required" aria-hidden="true">
                            *
                          </span>
                        </label>
                        <input
                          className="form-control justo-login__input"
                          autoComplete="username"
                          id="focus_1"
                          type="email"
                          placeholder={strings.emailPlaceholder}
                          value={loginDetail.email || ""}
                          onChange={(e) =>
                            setLoginDetail({
                              ...loginDetail,
                              email: e.target.value,
                            })
                          }
                          onKeyDown={handleEnterPressFromFirst}
                        />
                        {errorMessage.email && (
                          <span className="justo-login__error">
                            {errorMessage.email}
                          </span>
                        )}
                      </div>

                      <div className="justo-login__field">
                        <label className="justo-login__label" htmlFor="focus_2">
                          {strings.password}
                          <span className="justo-login__required" aria-hidden="true">
                            *
                          </span>
                        </label>
                        <div className="justo-login__input-group">
                          <input
                            className="form-control justo-login__input justo-login__input--password"
                            autoComplete="current-password"
                            id="focus_2"
                            type={passwordShown ? "text" : "password"}
                            placeholder={strings.passwordPlaceholder}
                            value={loginDetail.password || ""}
                            onChange={(e) =>
                              setLoginDetail({
                                ...loginDetail,
                                password: e.target.value,
                              })
                            }
                            onKeyDown={handleEnterPressFromSecond}
                          />
                          <button
                            type="button"
                            className="justo-login__password-toggle"
                            onClick={togglePassword}
                            aria-label={
                              passwordShown ? "Hide password" : "Show password"
                            }
                          >
                            <i
                              className={
                                passwordShown ? "fa fa-eye" : "fa fa-eye-slash"
                              }
                              aria-hidden="true"
                            />
                          </button>
                        </div>
                        {errorMessage.password && (
                          <span className="justo-login__error">
                            {errorMessage.password}
                          </span>
                        )}
                      </div>

                      <div className="justo-login__actions">
                        <button
                          type="button"
                          onClick={() => navigate("/forgotpassword")}
                          className="justo-login__forgot"
                        >
                          {strings.forgotPass}
                        </button>
                      </div>

                      <button
                        type="submit"
                        className="btn btn-primary btn-block justo-login__submit"
                        disabled={!canSubmit}
                      >
                        {strings.login}
                      </button>
                    </form>

                    <div className="justo-login__legal">
                      <button
                        type="button"
                        className="justo-login__legal-link"
                        onClick={() => navigate("/termandcondition")}
                      >
                        {strings.loginTermsLink}
                      </button>
                      <span className="justo-login__legal-dot" aria-hidden="true">
                        ·
                      </span>
                      <button
                        type="button"
                        className="justo-login__legal-link"
                        onClick={() => navigate("/privacypolicy")}
                      >
                        {strings.loginPrivacyLink}
                      </button>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default Signin;
